import React, { useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useAppMotion, EASE_EMPHASIS } from '../../hooks/useAppMotion';
import { haptic } from '../../utils/haptics';

/**
 * Segment-Umschalter (Okt 2026, „App wie Spot“): zwei bis vier gleichrangige
 * Optionen auf einer Chip-Spur, die gewaehlte liegt als helle Flaeche darauf
 * und gleitet beim Wechsel hinueber. So zeigt der Spot die Reiter der
 * Profilkarte (Skills | Zeiten | Info) und den Session-Zugang (# Code | QR).
 *
 * Vorher baute jede Stelle ihr eigenes Segment — oder nahm einen einzelnen
 * Knopf, der den jeweils anderen Modus anbot (SessionInfoCards). Der zeigte
 * damit nie, was gerade zu sehen ist.
 *
 * - as="tabs":  schaltet Ansichten um (role tablist/tab, aria-selected)
 * - as="radio": waehlt einen Wert (role radiogroup/radio, aria-checked)
 * - size:       md (36px, Profilkarte) | sm (32px, kompakt in Karten)
 * - options:    [{ value, label, icon? }]
 *
 * Aktiv ist Tinte auf heller Flaeche, nicht Lila: ein Umschalter ist kein
 * Aufruf zum Handeln (CorporateDesign, hoechstens drei lila Elemente).
 *
 * Haptik (Oktober 2026): Ein Wechsel löst `haptic.selection()` hier im
 * Baustein aus, Aufrufer ergänzen keine. Ein Tipp auf die schon gewählte
 * Option bleibt stumm.
 *
 * Größen auf dem Raster (Oktober 2026): Icons `h-4 w-4` in beiden Größen,
 * `sm` 32px hoch mit der Stufe `text-label` (13,5px) statt freier Pixelwerte.
 */

const SIZES = {
  md: { track: 'p-1 rounded-xl gap-1', item: 'h-9 text-sm rounded-lg', icon: 'h-4 w-4' },
  sm: { track: 'p-[3px] rounded-xl gap-1', item: 'h-8 text-label rounded-lg', icon: 'h-4 w-4' },
};

// Optional verkettet, weil Tests `utils/haptics` oft nur teilweise mocken.
const tick = () => haptic?.selection?.();

const SegmentedControl = ({
  options,
  value,
  onChange,
  size = 'md',
  as = 'tabs',
  ariaLabel,
  idPrefix,
  className = '',
}) => {
  const { reduced } = useAppMotion();
  const layoutId = `segment-${useId()}`;
  const refs = useRef([]);
  const s = SIZES[size] || SIZES.md;
  const isTabs = as === 'tabs';

  const handleKeyDown = (event, index) => {
    const last = options.length - 1;
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    if (options[next].value !== value) tick();
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role={isTabs ? 'tablist' : 'radiogroup'}
      aria-label={ariaLabel}
      className={`flex bg-chip ${s.track} ${className}`}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        const Icon = option.icon;
        const roleProps = isTabs
          ? {
            role: 'tab',
            'aria-selected': active,
            ...(idPrefix
              ? { id: `${idPrefix}-tab-${option.value}`, 'aria-controls': `${idPrefix}-panel-${option.value}` }
              : {}),
          }
          : { role: 'radio', 'aria-checked': active };
        return (
          <button
            key={option.value}
            ref={(el) => { refs.current[index] = el; }}
            type="button"
            {...roleProps}
            tabIndex={active || (value == null && index === 0) ? 0 : -1}
            onClick={() => {
              if (option.value !== value) tick();
              onChange(option.value);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={[
              'relative flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-2 font-medium',
              'transition-colors duration-fast touch-manipulation',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              s.item,
              active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 rounded-[inherit] bg-thumb shadow-token-sm"
                transition={{ duration: reduced ? 0 : 0.3, ease: EASE_EMPHASIS }}
                aria-hidden="true"
              />
            )}
            {Icon && <Icon className={`relative shrink-0 ${s.icon}`} aria-hidden="true" />}
            <span className="relative truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

SegmentedControl.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['md', 'sm']),
  as: PropTypes.oneOf(['tabs', 'radio']),
  ariaLabel: PropTypes.string.isRequired,
  /** setzt id/aria-controls fuer Tab-Panels: `${idPrefix}-panel-${value}` */
  idPrefix: PropTypes.string,
  className: PropTypes.string,
};

export default SegmentedControl;
